import React, { useMemo } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Percent, HandCoins, CalendarDays } from "lucide-react";
import { formatBRL, formatDate, FREQ_LABEL } from "./LoanDetailsDialog";
import { unpaidInstallments, interestShare } from "./LoansSummary";

const MONTHS_LONG = ["Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho", "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro"];

export function monthLabel(key) {
  const [y, m] = key.split("-");
  return `${MONTHS_LONG[parseInt(m, 10) - 1]} ${y}`;
}

export default function LoanMonthDialog({ monthKey, loans, customerById, today, onClose, onOpenLoan }) {
  const data = useMemo(() => {
    if (!monthKey) return null;
    const received = [];
    const scheduled = [];
    for (const l of loans) {
      const name = customerById.get(l.customer_id)?.name || "Cliente removido";
      const share = interestShare(l);
      for (const p of l.payments || []) {
        if (!p.date?.startsWith(monthKey)) continue;
        const interest = p.interest_only ? p.amount : p.amount * share;
        received.push({ loan: l, name, date: p.date, amount: p.amount, interest, interestOnly: !!p.interest_only });
      }
      if (l.status !== "paid") {
        for (const inst of unpaidInstallments(l)) {
          if (!inst.due?.startsWith(monthKey)) continue;
          scheduled.push({ loan: l, name, due: inst.due, amount: inst.amount, interest: inst.interest, late: inst.due < today });
        }
      }
    }
    received.sort((a, b) => a.date.localeCompare(b.date));
    scheduled.sort((a, b) => a.due.localeCompare(b.due));
    const sum = (arr, k) => arr.reduce((s, x) => s + x[k], 0);
    return {
      received,
      scheduled,
      interestReceived: sum(received, "interest"),
      amountReceived: sum(received, "amount"),
      revenueScheduled: sum(scheduled, "amount"),
      interestScheduled: sum(scheduled, "interest"),
    };
  }, [monthKey, loans, customerById, today]);

  if (!monthKey || !data) return null;

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="loan-month-dialog" className="flex max-h-[90vh] flex-col gap-3 overflow-hidden border-white/10 bg-[#121212] sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-display text-xl font-bold text-white">
            <CalendarDays className="h-5 w-5 text-[#A78BFA]" />
            Extrato · {monthLabel(monthKey)}
          </DialogTitle>
          <DialogDescription className="text-xs text-zinc-500">Juros recebidos e receita programada para o mês.</DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
          <Stat label="Juros recebidos" value={formatBRL(data.interestReceived)} color="#A78BFA" testid="month-interest-received" />
          <Stat label="Total recebido" value={formatBRL(data.amountReceived)} color="#22C55E" testid="month-amount-received" />
          <Stat label="Receita a receber" value={formatBRL(data.revenueScheduled)} color="#D4FF00" testid="month-revenue-scheduled" sub={`${data.scheduled.length} parcela${data.scheduled.length === 1 ? "" : "s"}`} />
          <Stat label="Juros a receber" value={formatBRL(data.interestScheduled)} color="#F59E0B" testid="month-interest-scheduled" />
        </div>

        <div className="min-h-0 flex-1 space-y-4 overflow-y-auto pr-1">
          <Section icon={Percent} color="#A78BFA" title="Juros recebidos" count={data.received.length} empty="Nenhum recebimento neste mês.">
            {data.received.map((r, i) => (
              <Row
                key={i}
                testid={`month-received-${i}`}
                onClick={() => onOpenLoan(r.loan)}
                left={
                  <>
                    <span className="font-semibold text-white">{r.name}</span>
                    <span className="text-zinc-500"> · {formatDate(r.date)} · {FREQ_LABEL[r.loan.frequency] || r.loan.frequency}</span>
                    {r.interestOnly && (
                      <span className="ml-1.5 rounded-sm bg-[#A78BFA]/20 px-1 py-0.5 text-[0.55rem] font-bold uppercase tracking-wider text-[#A78BFA]">Só juros</span>
                    )}
                  </>
                }
                right={
                  <>
                    <div className="font-mono-num font-bold text-[#A78BFA]">+{formatBRL(r.interest)}</div>
                    <div className="text-[0.62rem] text-zinc-500">de {formatBRL(r.amount)} pagos</div>
                  </>
                }
              />
            ))}
          </Section>

          <Section icon={HandCoins} color="#D4FF00" title="Receita programada" count={data.scheduled.length} empty="Nenhuma parcela programada neste mês.">
            {data.scheduled.map((r, i) => (
              <Row
                key={i}
                testid={`month-scheduled-${i}`}
                onClick={() => onOpenLoan(r.loan)}
                left={
                  <>
                    <span className="font-semibold text-white">{r.name}</span>
                    <span className="text-zinc-500"> · vence {formatDate(r.due)}</span>
                    {r.late && (
                      <span className="ml-1.5 rounded-sm bg-red-500/20 px-1 py-0.5 text-[0.55rem] font-bold uppercase tracking-wider text-red-300">Atrasado</span>
                    )}
                  </>
                }
                right={
                  <>
                    <div className="font-mono-num font-bold text-[#D4FF00]">{formatBRL(r.amount)}</div>
                    <div className="text-[0.62rem] text-zinc-500">juros {formatBRL(r.interest)}</div>
                  </>
                }
              />
            ))}
          </Section>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Stat({ label, value, color, testid, sub }) {
  return (
    <div data-testid={testid} className="rounded-lg border border-white/10 bg-black/30 p-3">
      <div className="text-[0.62rem] uppercase tracking-widest text-zinc-500">{label}</div>
      <div className="font-mono-num text-base font-bold tabular-nums" style={{ color }}>{value}</div>
      {sub && <div className="text-[0.62rem] text-zinc-500">{sub}</div>}
    </div>
  );
}

function Section({ icon: Icon, color, title, count, empty, children }) {
  return (
    <div>
      <div className="mb-1.5 flex items-center gap-2 text-[0.7rem] uppercase tracking-widest text-zinc-400">
        <Icon className="h-3.5 w-3.5" style={{ color }} />
        {title} <span className="text-zinc-600">({count})</span>
      </div>
      {count === 0 ? (
        <div className="rounded-lg border border-dashed border-white/10 px-3 py-3 text-xs text-zinc-600">{empty}</div>
      ) : (
        <div className="overflow-hidden rounded-lg border border-white/10">{children}</div>
      )}
    </div>
  );
}

function Row({ left, right, onClick, testid }) {
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      className="flex w-full items-center justify-between gap-3 border-t border-white/5 bg-black/20 px-3 py-2 text-left text-xs first:border-t-0 hover:bg-white/[0.04]"
    >
      <div className="min-w-0 flex-1 truncate">{left}</div>
      <div className="shrink-0 text-right">{right}</div>
    </button>
  );
}
